import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, RotateCcw, Target, X } from 'lucide-react';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import usePageTitle from '../../hooks/usePageTitle';
import BrandLogo from '../../components/ui/BrandLogo';
import ScopeIllustration from '../../components/ui/ScopeIllustration';
import SiteFooter from '../../components/layout/SiteFooter';
import { POOL_ATSEM as CLAUDE_POOL_ATSEM, POOL_GENERIC as CLAUDE_POOL_GENERIC } from '../admin/AdminLandingClaudeHybride';
import '../../css/admin/landingcodexclaude.css';
import '../../css/admin/landingcodexhybride.css';

// Satellites de révisions espacées (classes ldc-* du thème éditorial partagé).
function MemoryVisual() {
    return (
        <div className="ldc-memory-visual" aria-label="Illustration de trois révisions espacées">
            <span className="ldc-orbit ldc-orbit--one" />
            <span className="ldc-orbit ldc-orbit--two" />
            <span className="ldc-orbit ldc-orbit--three" />
            <div className="ldc-memory-core"><small>notion</small><strong>acquise</strong></div>
            <span className="ldc-memory-date ldc-memory-date--one">J+1</span>
            <span className="ldc-memory-date ldc-memory-date--two">J+3</span>
            <span className="ldc-memory-date ldc-memory-date--three">J+8</span>
        </div>
    );
}

const FALLBACK_QUIZ_PACKS = {
    generic: {
        label: 'Culture concours',
        intro: 'Un aperçu transversal, sans spécialité imposée',
        subjects: {
            logique: { label: 'Logique et raisonnement', advice: 'Renforcer les exercices de déduction et de suites.' },
            comprehension: { label: 'Français et compréhension', advice: 'Travailler la lecture précise des formulations et des exceptions.' },
            methode: { label: 'Méthode et priorisation', advice: 'Structurer la manière d’aborder une question et d’exploiter une erreur.' },
        },
        previewMastery: [
            { subjectKey: 'logique', pct: 74, tone: 'strong' },
            { subjectKey: 'comprehension', pct: 58, tone: 'confirm' },
            { subjectKey: 'methode', pct: 66, tone: 'confirm' },
        ],
        direction: {
            title: 'Ta moyenne ne raconte pas toute l’histoire.',
            text: 'Logique, compréhension et méthode peuvent évoluer différemment. La progression par sujet montre ce qui mérite réellement ta prochaine séance.',
        },
        questions: [
            {
                subjectKey: 'logique',
                prompt: 'Complétez la suite : 2, 6, 12, 20, 30, … ?',
                answers: [['a', '36'], ['b', '40'], ['c', '42'], ['d', '44']],
                correct: 'c',
                explanation: 'Les écarts sont +4, +6, +8, +10, puis +12 : la réponse est 42.',
            },
            {
                subjectKey: 'comprehension',
                prompt: 'Quelle phrase est correctement écrite ?',
                answers: [
                    ['a', 'Les informations qu’elle a transmit sont exactes.'],
                    ['b', 'Les information qu’elle a transmises sont exactes.'],
                    ['c', 'Les informations qu’elle à transmises sont exactes.'],
                    ['d', 'Les informations qu’elle a transmises sont exactes.'],
                ],
                correct: 'd',
                explanation: '« Informations » est pluriel et le participe passé « transmises » s’accorde avec le complément placé avant le verbe.',
            },
            {
                subjectKey: 'methode',
                prompt: 'Face à une question de concours difficile, quelle démarche est la plus efficace ?',
                answers: [
                    ['a', 'Répondre immédiatement pour ne pas perdre de temps'],
                    ['b', 'Relire la consigne, repérer les mots décisifs puis éliminer les réponses incompatibles'],
                    ['c', 'Choisir systématiquement la proposition la plus longue'],
                    ['d', 'Abandonner la question sans examiner les réponses'],
                ],
                correct: 'b',
                explanation: 'Une lecture précise suivie d’une élimination raisonnée limite les erreurs provoquées par les distracteurs.',
            },
            {
                subjectKey: 'logique',
                prompt: 'Tous les dossiers urgents sont traités aujourd’hui. Le dossier X n’est pas traité aujourd’hui. Que peut-on en déduire ?',
                answers: [
                    ['a', 'Le dossier X n’est pas urgent'],
                    ['b', 'Le dossier X est incomplet'],
                    ['c', 'Le dossier X sera prioritaire demain'],
                    ['d', 'Aucune conclusion n’est possible'],
                ],
                correct: 'a',
                explanation: 'Si tous les dossiers urgents sont traités aujourd’hui, un dossier non traité aujourd’hui ne peut pas appartenir à cet ensemble.',
            },
            {
                subjectKey: 'comprehension',
                prompt: 'Une règle prévoit : « Tout dossier incomplet est retourné, sauf si la pièce manquante peut être obtenue auprès d’un autre service. » La pièce manquante du dossier de Léa est disponible auprès d’un autre service. Que permet la règle ?',
                answers: [
                    ['a', 'Le dossier doit obligatoirement être retourné'],
                    ['b', 'Le dossier peut être complété sans être retourné'],
                    ['c', 'Le dossier est automatiquement accepté'],
                    ['d', 'La pièce manquante devient facultative'],
                ],
                correct: 'b',
                explanation: 'L’exception autorise l’obtention de la pièce auprès de l’autre service ; elle ne rend ni le dossier accepté ni la pièce facultative.',
            },
            {
                subjectKey: 'methode',
                prompt: 'Après une mauvaise réponse, quelle action favorise le mieux un apprentissage durable ?',
                answers: [
                    ['a', 'Mémoriser uniquement la lettre de la bonne réponse'],
                    ['b', 'Recommencer immédiatement tout le programme'],
                    ['c', 'Comprendre la correction puis revoir la notion lors d’une prochaine séance'],
                    ['d', 'Écarter définitivement cette question du programme'],
                ],
                correct: 'c',
                explanation: 'Comprendre l’écart puis récupérer à nouveau la notion plus tard consolide davantage que la simple mémorisation de la réponse.',
            },
        ],
    },
    atsem: {
        label: 'Concours ATSEM',
        intro: 'Une micro-séance adaptée à votre préparation ATSEM',
        subjects: {
            cadre: { label: 'Rôle et responsabilités', advice: 'Revoir les limites d’initiative et les relais de responsabilité.' },
            hygiene: { label: 'Hygiène, sécurité et soins', advice: 'Reprendre les protocoles de soins et le stockage sécurisé.' },
            accompagnement: { label: 'Accompagnement et communication', advice: 'Travailler l’aide graduée, l’intimité et la transmission.' },
        },
        previewMastery: [
            { subjectKey: 'cadre', pct: 86, tone: 'strong' },
            { subjectKey: 'hygiene', pct: 58, tone: 'confirm' },
            { subjectKey: 'accompagnement', pct: 74, tone: 'strong' },
        ],
        direction: {
            title: 'Ta moyenne ne dit pas où renforcer ta pratique.',
            text: 'Rôle de l’ATSEM, hygiène et accompagnement ne progressent pas toujours au même rythme. La vue par sujet rend la prochaine priorité concrète.',
        },
        questions: [
            {
                subjectKey: 'cadre',
                prompt: 'Lors d’une transition vers le périscolaire, le professionnel attendu n’est pas présent. Que doit faire l’ATSEM ?',
                answers: [
                    ['a', 'Attendre sans signaler l’absence'],
                    ['b', 'Maintenir la surveillance et alerter selon la procédure'],
                    ['c', 'Confier le groupe à un agent communal disponible'],
                    ['d', 'Conduire les enfants à l’accueil sans vérifier le relais'],
                ],
                correct: 'b',
                explanation: 'La responsabilité ne change qu’après une prise en charge effective par la personne prévue.',
            },
            {
                subjectKey: 'hygiene',
                prompt: 'Un enfant présente une petite éraflure. Quelle conduite est la plus adaptée pour l’ATSEM ?',
                answers: [
                    ['a', 'Choisir une crème antiseptique dans l’armoire'],
                    ['b', 'Appliquer le protocole de soins de l’école et transmettre l’information'],
                    ['c', 'Donner un médicament contre la douleur'],
                    ['d', 'Attendre les parents sans effectuer les mesures d’hygiène prévues'],
                ],
                correct: 'b',
                explanation: 'L’ATSEM applique le protocole prévu sans choisir de sa propre initiative un médicament, une crème ou un antiseptique.',
            },
            {
                subjectKey: 'accompagnement',
                prompt: 'Un enfant essaie de fermer son manteau mais n’y parvient pas immédiatement. Quelle attitude favorise son autonomie ?',
                answers: [
                    ['a', 'Fermer entièrement le manteau à sa place pour aller plus vite'],
                    ['b', 'Laisser l’enfant seul jusqu’à ce qu’il réussisse'],
                    ['c', 'Le laisser essayer, verbaliser les étapes puis apporter une aide graduée'],
                    ['d', 'Demander systématiquement à un autre enfant de le faire'],
                ],
                correct: 'c',
                explanation: 'L’autonomie se construit en laissant une initiative réelle à l’enfant et en ajustant l’aide à son besoin.',
            },
            {
                subjectKey: 'cadre',
                prompt: 'Le repas prévu par le PAI d’un enfant n’est pas disponible. Que doit faire l’ATSEM ?',
                answers: [
                    ['a', 'Composer elle-même un repas équivalent'],
                    ['b', 'Supprimer uniquement l’aliment qui lui semble problématique'],
                    ['c', 'Appliquer les consignes prévues et alerter le responsable sans improviser'],
                    ['d', 'Demander à l’enfant de choisir parmi les autres repas'],
                ],
                correct: 'c',
                explanation: 'Le PAI s’applique tel qu’il est défini. Une difficulté d’application doit être signalée, pas compensée par une initiative personnelle.',
            },
            {
                subjectKey: 'hygiene',
                prompt: 'Comment les produits d’entretien doivent-ils être conservés dans l’école ?',
                answers: [
                    ['a', 'En hauteur dans la classe, même si le placard reste ouvert'],
                    ['b', 'Dans leur emballage d’origine et dans un espace fermé ou sécurisé'],
                    ['c', 'Transvasés dans de petits contenants plus faciles à utiliser'],
                    ['d', 'Sous un évier si les enfants sont informés du danger'],
                ],
                correct: 'b',
                explanation: 'La mise en hauteur ne suffit pas : les produits restent dans leur emballage d’origine et hors d’accès, dans un stockage fermé ou sécurisé.',
            },
            {
                subjectKey: 'accompagnement',
                prompt: 'Un enfant a un incident de propreté pendant la classe. Quelle réponse est la plus professionnelle ?',
                answers: [
                    ['a', 'L’accompagner discrètement, le rassurer et appliquer les mesures d’hygiène'],
                    ['b', 'Lui demander de tout gérer seul pour développer son autonomie'],
                    ['c', 'Expliquer l’incident devant le groupe pour éviter qu’il se reproduise'],
                    ['d', 'Mettre le linge souillé dans son sac sans protection particulière'],
                ],
                correct: 'a',
                explanation: 'L’accompagnement protège l’intimité de l’enfant, respecte l’hygiène et maintient une attitude rassurante et bienveillante.',
            },
        ],
    },
};

const CLAUDE_SUBJECTS = {
    generic: {
        'Logique & raisonnement': { key: 'logique', advice: 'Renforcer les exercices de déduction et de suites.' },
        Institutions: { key: 'institutions', advice: 'Revoir les principaux repères institutionnels et territoriaux.' },
        Français: { key: 'francais', advice: 'Travailler les accords et la lecture précise des formulations.' },
    },
    atsem: {
        'Sécurité & surveillance': { key: 'securite', advice: 'Reprendre les conduites à tenir et les relais de responsabilité.' },
        'Santé & PAI': { key: 'sante', advice: 'Revoir les limites d’initiative médicale et l’application du PAI.' },
        Hygiène: { key: 'hygiene', advice: 'Renforcer les protocoles d’hygiène et de sécurité alimentaire.' },
    },
};

const DIRECTION_BY_PACK = {
    generic: {
        intro: 'Un audit mélangé sur trois sujets de concours',
        title: 'Ta moyenne ne raconte pas toute l’histoire.',
        text: 'Culture générale, logique, français et institutions peuvent évoluer différemment. La progression par sujet montre ce qui mérite réellement ta prochaine séance.',
    },
    atsem: {
        intro: 'Un audit adapté à votre préparation ATSEM',
        title: 'Ta moyenne ne dit pas où renforcer ta pratique.',
        text: 'Rôle de l’ATSEM, sécurité, hygiène et périscolaire ne progressent pas toujours au même rythme. La vue par sujet rend la prochaine priorité concrète.',
    },
};

function normalizeClaudePool(source, packKey, fallback) {
    if (!source?.questions?.length) return fallback;
    const subjectConfig = CLAUDE_SUBJECTS[packKey];
    const subjects = Object.fromEntries(Object.entries(subjectConfig).map(([label, config]) => [
        config.key,
        { label, advice: config.advice },
    ]));
    const toneMap = { ok: 'strong', warn: 'confirm', new: 'priority' };
    return {
        label: source.label,
        intro: DIRECTION_BY_PACK[packKey].intro,
        subjects,
        previewMastery: source.mastery.map((row, index) => ({
            key: `preview-${packKey}-${index}`,
            label: row.label,
            pct: row.pct,
            tone: toneMap[row.tone] || 'confirm',
        })),
        direction: DIRECTION_BY_PACK[packKey],
        questions: source.questions.map(question => ({
            subjectKey: subjectConfig[question.sujet].key,
            prompt: question.enonce,
            answers: question.reponses,
            correct: question.bonne,
            explanation: question.explication,
        })),
    };
}

// Source de référence : les pools éditoriaux de /landingclaudehybride.
const QUIZ_PACKS = {
    generic: normalizeClaudePool(CLAUDE_POOL_GENERIC, 'generic', FALLBACK_QUIZ_PACKS.generic),
    atsem: normalizeClaudePool(CLAUDE_POOL_ATSEM, 'atsem', FALLBACK_QUIZ_PACKS.atsem),
};

function getStoredAcquisitionSignal() {
    try {
        const acquisition = JSON.parse(localStorage.getItem('mq_acq') || '{}');
        return [acquisition.utm_source, acquisition.utm_medium, acquisition.utm_campaign, acquisition.referrer, acquisition.landing_page]
            .filter(Boolean).join(' ');
    } catch {
        return '';
    }
}

function resolveQuizPack(searchParams) {
    const explicit = ['theme', 'scope', 'quiz', 'from', 'utm_source', 'utm_campaign', 'utm_content']
        .map(key => searchParams.get(key) || '')
        .join(' ');
    const referrer = typeof document !== 'undefined' ? document.referrer : '';
    const stored = typeof localStorage !== 'undefined' ? getStoredAcquisitionSignal() : '';
    const signal = `${explicit} ${referrer} ${stored}`.toLowerCase();
    return signal.includes('atsem') ? 'atsem' : 'generic';
}

function useRevealOnce(threshold = 0.35) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return undefined;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setVisible(true);
                observer.disconnect();
            }
        }, { threshold });
        observer.observe(element);
        return () => observer.disconnect();
    }, [threshold]);

    return [ref, visible];
}

function buildAuditSummary(pack, results) {
    const subjects = Object.entries(pack.subjects).map(([key, subject]) => {
        const answers = results.filter(result => result.subjectKey === key);
        const correct = answers.filter(result => result.correct).length;
        return {
            key,
            ...subject,
            correct,
            total: answers.length,
            pct: answers.length ? Math.round((correct / answers.length) * 100) : 0,
            status: correct === 2 ? 'Point fort observé' : correct === 1 ? 'À confirmer' : 'Priorité suggérée',
            tone: correct === 2 ? 'strong' : correct === 1 ? 'confirm' : 'priority',
        };
    });
    const priority = [...subjects].sort((a, b) => a.correct - b.correct)[0];
    return { subjects, priority, allStrong: subjects.every(subject => subject.correct === 2) };
}

function MiniSession({ packKey, onComplete, onReset }) {
    const pack = QUIZ_PACKS[packKey];
    const quizLabel = packKey === 'atsem' ? 'Spécial concours ATSEM' : 'Aperçu général';
    const [index, setIndex] = useState(0);
    const [selection, setSelection] = useState(null);
    const [revealed, setRevealed] = useState(false);
    const [results, setResults] = useState([]);
    const question = pack.questions[index];
    const finished = index >= pack.questions.length;

    useEffect(() => {
        setIndex(0);
        setSelection(null);
        setRevealed(false);
        setResults([]);
    }, [packKey]);

    const choose = (id) => {
        if (revealed) return;
        const correct = id === question.correct;
        setSelection(id);
        setRevealed(true);
        setResults(previous => [...previous, { correct, subjectKey: question.subjectKey }]);
    };

    const next = () => {
        if (index === pack.questions.length - 1) onComplete?.(buildAuditSummary(pack, results));
        setIndex(previous => previous + 1);
        setSelection(null);
        setRevealed(false);
    };

    const restart = () => {
        setIndex(0);
        setSelection(null);
        setRevealed(false);
        setResults([]);
        onReset?.();
    };

    if (finished) {
        const score = results.filter(result => result.correct).length;
        const { subjects: subjectResults, priority, allStrong } = buildAuditSummary(pack, results);
        return (
            <div className="ldh-quiz ldh-quiz--complete">
                <div className="ldh-report-head">
                    <div><span className="ldh-quiz-context">Premier bilan terminé</span><h3>{score >= 5 ? 'Des bases solides se dessinent.' : 'Une première direction se dessine.'}</h3></div>
                    <div className="ldh-score"><strong>{score}</strong><span>/ 6</span></div>
                </div>
                <div className="ldh-subject-report">
                    {subjectResults.map(subject => (
                        <div className={`ldh-subject-row ldh-subject-row--${subject.tone}`} key={subject.key}>
                            <div><strong>{subject.label}</strong><span>{subject.status}</span></div>
                            <div className="ldh-subject-track"><i style={{ width: `${(subject.correct / 2) * 100}%` }} /></div>
                            <b>{subject.correct}/2</b>
                        </div>
                    ))}
                </div>
                <div className="ldh-priority">
                    <span>{allStrong ? 'Pour poursuivre' : 'Prochaine priorité suggérée'}</span>
                    <strong>{allStrong ? 'Élargir le diagnostic' : priority.label}</strong>
                    <p>{allStrong ? 'Ces trois axes sont bien engagés ; un audit plus large permettra de confirmer ce premier résultat.' : priority.advice}</p>
                </div>
                <p className="ldh-report-caveat">Six questions donnent un premier repère. L’audit complet affine ce bilan sur davantage de questions.</p>
                <div className="ldh-quiz-final-actions">
                    <Link to="/inscription" className="ldc-btn ldc-btn--dark">Continuer avec un compte gratuit <ArrowRight size={16} /></Link>
                    <button type="button" onClick={restart}><RotateCcw size={15} /> Rejouer</button>
                </div>
            </div>
        );
    }

    const correctNow = revealed && selection === question.correct;
    return (
        <div className="ldh-quiz">
            <div className="ldh-quiz-head">
                <div><span className="ldh-quiz-context">{quizLabel}</span><small>3 sujets · bilan détaillé à la fin</small></div>
                <strong>{String(index + 1).padStart(2, '0')} <i>/ 06</i></strong>
            </div>
            <div className="ldh-quiz-progress"><i style={{ width: `${((index + (revealed ? 1 : 0)) / 6) * 100}%` }} /></div>
            <div className="ldh-question-stage" key={`${packKey}-${index}`}>
                <span className="ldh-format">QCM · une seule réponse attendue</span>
                <h3>{question.prompt}</h3>
                <div className="ldh-answers">
                    {question.answers.map(([id, label]) => {
                        const picked = selection === id;
                        const good = revealed && question.correct === id;
                        const bad = revealed && picked && question.correct !== id;
                        return (
                            <button
                                type="button"
                                key={id}
                                className={`${picked ? 'is-picked' : ''} ${good ? 'is-good' : ''} ${bad ? 'is-bad' : ''}`}
                                onClick={() => choose(id)}
                                disabled={revealed}
                            >
                                <span>{id.toUpperCase()}</span>
                                {label}
                                {good && <Check size={17} />}
                                {bad && <X size={17} />}
                            </button>
                        );
                    })}
                </div>
                {revealed && (
                    <div className={`ldh-explanation ${correctNow ? 'is-correct' : ''}`}>
                        <div><strong>{correctNow ? 'Bien vu.' : 'À revoir.'}</strong><p>{question.explanation}</p></div>
                        <button type="button" onClick={next}>{index === 5 ? 'Voir mon bilan' : 'Question suivante'} <ArrowRight size={15} /></button>
                    </div>
                )}
            </div>
        </div>
    );
}

function MasteryCard({ pack, auditSummary }) {
    const [ref, visible] = useRevealOnce(0.35);
    const rows = auditSummary
        ? auditSummary.subjects
        : pack.previewMastery.map(row => ({
            ...row,
            key: row.key || row.subjectKey,
            label: row.label || pack.subjects[row.subjectKey]?.label,
        }));
    const priority = auditSummary?.priority || rows.find(row => row.tone === 'confirm') || [...rows].sort((a, b) => a.pct - b.pct)[0];
    const note = auditSummary?.allStrong
        ? 'Premier repère homogène : élargir le diagnostic'
        : `Prochaine séance : priorité « ${priority.label} »`;

    return (
        <div className={`ldh-mastery ${visible ? 'is-visible' : ''}`} ref={ref}>
            <div className="ldh-mastery-head">
                <strong>Maîtrise par sujet</strong>
                <span>{auditSummary ? 'd’après tes 6 réponses' : 'exemple de progression'}</span>
            </div>
            {rows.map((row, index) => (
                <div className="ldh-mastery-row" key={row.key || row.subjectKey}>
                    <span>{row.label}</span>
                    <div className="ldh-mastery-track">
                        <i
                            className={`ldh-mastery-fill ldh-mastery-fill--${row.tone}`}
                            style={{ '--mastery-width': `${row.pct}%`, transitionDelay: `${0.15 + index * 0.15}s` }}
                        />
                    </div>
                    <strong>{row.pct} %</strong>
                </div>
            ))}
            <div className="ldh-mastery-note"><Target size={15} /> {note}</div>
        </div>
    );
}

function ForgettingCurve() {
    const [ref, drawn] = useRevealOnce(0.4);
    return (
        <svg ref={ref} viewBox="0 0 560 270" className={`ldh-curve ${drawn ? 'is-drawn' : ''}`} role="img" aria-label="Comparaison entre oubli sans révision et consolidation avec trois révisions espacées">
            {[70, 130, 190].map(y => <line key={y} x1="42" y1={y} x2="535" y2={y} className="ldh-curve-grid" />)}
            <line x1="42" y1="225" x2="535" y2="225" className="ldh-curve-axis" />
            <path d="M42 35 C105 140 170 187 285 207 C390 222 485 224 535 225" className="ldh-curve-decay" />
            <path d="M42 35 C72 82 92 111 112 126 L112 45 C152 85 180 101 212 111 L212 49 C275 80 320 88 374 92 L374 55 C440 69 497 70 535 71" className="ldh-curve-spaced" pathLength="1000" />
            {[[112, 45, 'J+1'], [212, 49, 'J+3'], [374, 55, 'J+8']].map(([x, y, label], index) => (
                <g key={label} className="ldh-curve-milestone" style={{ transitionDelay: `${0.9 + index * 0.35}s` }}>
                    <circle cx={x} cy={y} r="6" /><text x={x} y={y - 14} textAnchor="middle">{label}</text>
                </g>
            ))}
            <text x="428" y="211" className="ldh-curve-red">sans révision</text>
            <text x="407" y="52" className="ldh-curve-green">avec révisions</text>
            <text x="288" y="254" textAnchor="middle" className="ldh-curve-time">temps</text>
        </svg>
    );
}

const STEPS = [
    ['01', 'Se tester', 'Tu réponds de mémoire, sans filet : c’est cet effort qui ancre.'],
    ['02', 'Comprendre', 'La correction t’explique le pourquoi, pas juste la bonne réponse.'],
    ['03', 'Consolider', 'La notion revient juste avant que tu ne l’oublies.'],
    ['04', 'Décider', 'Ta progression par sujet te montre quoi travailler ensuite.'],
];

const HERO_STORIES = 4;

// Démonstration produit propre à l'hybride Codex. La carte de l'hybride
// Claude reste volontairement intacte : ici, le hero déroule une mini-histoire
// complète, de la notion à revoir jusqu'à la prochaine révision planifiée.
function CinematicHeroCard() {
    const [scene, setScene] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
        const timer = window.setInterval(() => setScene(current => (current + 1) % HERO_STORIES), 4000);
        return () => window.clearInterval(timer);
    }, [paused]);

    return (
        <div
            className={`ldc-hero-float ldc-live ldh-hero-live is-scene-${scene}`}
            aria-hidden="true"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="ldh-live-head">
                <span><i /> MonQuiz en action</span>
                <div className="ldh-live-dots">
                    {Array.from({ length: HERO_STORIES }, (_, index) => (
                        <b key={index} className={index === scene ? 'is-active' : ''} />
                    ))}
                </div>
            </div>

            {scene === 0 && (
                <div className="ldc-live-scene" key="review">
                    <span>À revoir aujourd'hui</span>
                    <strong>6 notions</strong>
                    <div className="ldc-live-bars"><i /><i /><i /><i /><i /><i /></div>
                </div>
            )}
            {scene === 1 && (
                <div className="ldc-live-scene" key="session">
                    <span>Session en cours</span>
                    <strong>4 / 6 réponses</strong>
                    <div className="ldc-live-progress"><i /></div>
                </div>
            )}
            {scene === 2 && (
                <div className="ldc-live-scene ldh-live-success" key="success">
                    <span className="ldc-live-ok"><Check size={13} /> Réponse consolidée</span>
                    <strong><em>+12 %</em> de maîtrise</strong>
                    <div className="ldh-live-gain"><i /></div>
                </div>
            )}
            {scene === 3 && (
                <div className="ldc-live-scene" key="schedule">
                    <span className="ldc-live-ok"><Check size={12} /> Prochaine révision planifiée</span>
                    <strong>La mémoire se renforce</strong>
                    <div className="ldc-live-dates"><b>J+1</b><b className="is-next">J+3</b><b>J+8</b></div>
                </div>
            )}
        </div>
    );
}

export default function Home() {
    usePageTitle(null); // titre par défaut (SEO géré par index.php)
    const { user } = useAuthStore();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const packKey = useMemo(() => resolveQuizPack(searchParams), [searchParams]);
    const pack = QUIZ_PACKS[packKey];
    const [auditSummary, setAuditSummary] = useState(null);
    const [stats, setStats] = useState({ questions: null, scopes: null, prixMin: 3 });
    const [scopes, setScopes] = useState([]);
    const [partnerScopes, setPartnerScopes] = useState([]);
    const pageRef = useRef(null);
    const heroRef = useRef(null);

    useEffect(() => setAuditSummary(null), [packKey]);

    useEffect(() => {
        api.get('/scopes/list.php').then(({ data }) => {
            // list.php renvoie le tableau de scopes directement dans data
            const all = Array.isArray(data?.data) ? data.data : [];
            const editorial = all.filter(scope => (scope.content_source || 'editorial') === 'editorial'
                && scope.slug !== 'thematique-personnalisable' && scope.slug !== 'mes-thematiques');
            // Publications partenaires : catalogue à part, signé par son auteur.
            setPartnerScopes(all.filter(scope => scope.content_source === 'partner'));
            const prices = editorial.map(scope => Number(scope.price_1m)).filter(price => price > 0);
            setScopes(editorial);
            setStats({
                questions: editorial.reduce((sum, scope) => sum + (scope.nb_questions || 0), 0) || null,
                scopes: editorial.length || null,
                prixMin: prices.length ? Math.min(...prices) : 3,
            });
        }).catch(() => {});
    }, []);

    // Scroll vers une ancre quand on arrive via /#section (liens du header depuis
    // n'importe quelle page). La section #thematiques charge ses scopes en async
    // → retry jusqu'à la trouver, puis correction post-layout (même mécanique que
    // l'ancienne home).
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.slice(1);
        let cancelled = false;
        let attempts = 0;
        const timers = [];
        const scrollNow = () => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return !!el;
        };
        const tryScroll = () => {
            if (cancelled) return;
            if (scrollNow()) {
                timers.push(setTimeout(scrollNow, 500));
            } else if (attempts++ < 30) {
                timers.push(setTimeout(tryScroll, 80));
            }
        };
        timers.push(setTimeout(tryScroll, 50));
        return () => { cancelled = true; timers.forEach(clearTimeout); };
    }, [location.hash, location.key]);

    // Révélation au scroll — RE-SCAN quand les scopes arrivent : la section
    // catalogue se monte après la réponse API, ses éléments n'existent pas au
    // premier passage (sinon ils resteraient invisibles, opacity 0).
    useEffect(() => {
        const root = pageRef.current;
        if (!root) return undefined;
        const revealItems = [...root.querySelectorAll('[data-ldh-reveal]:not(.is-visible)')];
        if (!revealItems.length) return undefined;
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion || !('IntersectionObserver' in window)) {
            revealItems.forEach(item => item.classList.add('is-visible'));
            return undefined;
        }

        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
            });
        }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' });

        revealItems.forEach(item => observer.observe(item));
        return () => observer.disconnect();
    }, [scopes.length]);

    const directionTitle = auditSummary ? 'Ton essai devient une direction.' : pack.direction.title;
    const directionText = auditSummary
        ? auditSummary.allStrong
            ? 'Tes trois sujets sont bien engagés sur ce premier échantillon. Un audit plus large permettrait maintenant de confirmer ce résultat.'
            : `Sur cet essai, « ${auditSummary.priority.label} » ressort comme la première piste à renforcer. La vue par sujet transforme le score en prochaine action.`
        : pack.direction.text;

    const handleHeroPointerMove = event => {
        const hero = heroRef.current;
        if (!hero || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
        const rect = hero.getBoundingClientRect();
        const x = Math.max(-0.5, Math.min(0.5, (event.clientX - rect.left) / rect.width - 0.5));
        const y = Math.max(-0.5, Math.min(0.5, (event.clientY - rect.top) / rect.height - 0.5));
        hero.style.setProperty('--ldh-photo-x', `${x * -12}px`);
        hero.style.setProperty('--ldh-photo-y', `${y * -8}px`);
        hero.style.setProperty('--ldh-card-x', `${x * 14}px`);
        hero.style.setProperty('--ldh-card-y', `${y * 10}px`);
        hero.style.setProperty('--ldh-glow-x', `${50 + x * 18}%`);
        hero.style.setProperty('--ldh-glow-y', `${55 + y * 16}%`);
    };

    const resetHeroDepth = () => {
        const hero = heroRef.current;
        if (!hero) return;
        ['--ldh-photo-x', '--ldh-photo-y', '--ldh-card-x', '--ldh-card-y'].forEach(name => hero.style.setProperty(name, '0px'));
        hero.style.setProperty('--ldh-glow-x', '50%');
        hero.style.setProperty('--ldh-glow-y', '55%');
    };

    const scrollToSession = event => {
        event.preventDefault();
        document.getElementById('essai')?.scrollIntoView({
            behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
        });
    };

    return (
        <div className="ldc ldh" ref={pageRef}>
            <section
                ref={heroRef}
                className="ldc-hero ldh-hero ldh-hero--cinematic"
                onPointerMove={handleHeroPointerMove}
                onPointerLeave={resetHeroDepth}
            >
                <img src="/images/landing/monquiz-learning-hero.png" alt="Candidat préparant son concours avec une tablette et des fiches" />
                <div className="ldc-hero-shade" />
                <div className="ldh-hero-glow" aria-hidden="true" />
                <div className="ldc-hero-copy">
                    <BrandLogo width={170} hideTagline monColor="#fff" className="ldc-hero-logo" />
                    <span className="ldc-kicker"><i /> Une préparation qui apprend de tes réponses</span>
                    <h1 className="ldh-hero-title">
                        <span>Ne révise plus</span>
                        <span>au hasard.</span>
                        <span><em>Retiens plus.</em></span>
                    </h1>
                    <p>Teste ce que tu sais, comprends ce qui manque et revois chaque notion au moment où elle en a besoin.</p>
                    <div className="ldc-actions">
                        {user ? (
                            <Link to="/compte" className="ldc-btn ldc-btn--light ldh-hero-primary">Accéder à mes quiz <ArrowRight size={17} /></Link>
                        ) : (
                            <Link to="/inscription" className="ldc-btn ldc-btn--light ldh-hero-primary">Créer mon compte gratuit <ArrowRight size={17} /></Link>
                        )}
                        <a href="#essai" onClick={scrollToSession} className="ldc-btn ldc-btn--ghost">Faire la micro-séance</a>
                    </div>
                    {user ? (
                        <small>Reprends là où tu t'étais arrêté·e</small>
                    ) : (
                        <small>Sans carte bancaire · déjà inscrit ? <Link to="/connexion">Se connecter</Link></small>
                    )}
                </div>
                <CinematicHeroCard />
            </section>

            <div className="ldc-facts">
                <span><strong>{stats.questions ? stats.questions.toLocaleString('fr-FR') : '≈ 4 000'}</strong> questions validées</span><i />
                <span><strong>{stats.scopes ?? 7}</strong> thématiques</span><i />
                <span><strong>3 IA + 1 humain</strong> pour croiser la relecture</span>
            </div>

            <section className="ldh-promise" id="methode">
                <span className="ldc-section-no" data-ldh-reveal="up">La boucle MonQuiz</span>
                <h2 data-ldh-reveal="up" style={{ '--ldh-reveal-delay': '80ms' }}>Une séance courte.<br /><em>Quatre effets qui durent.</em></h2>
                <div className="ldh-step-line" data-ldh-reveal="line">
                    {STEPS.map(([num, title, text], index) => (
                        <article key={num} data-ldh-reveal="up" style={{ '--ldh-reveal-delay': `${160 + index * 90}ms` }}>
                            <span>{num}</span><h3>{title}</h3><p>{text}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="ldh-session" id="essai">
                <div className="ldh-session-copy" data-ldh-reveal="left">
                    <span className="ldc-section-no">L’expérience avant le discours</span>
                    <h2>Six questions.<br />Trois sujets à situer.</h2>
                    <p>{pack.intro}. Deux QCM par sujet, quatre propositions et une correction expliquée après chaque réponse.</p>
                    {packKey === 'atsem' && <span className="ldh-adapted"><i /> Questions adaptées au concours ATSEM</span>}
                    {packKey === 'generic' && <span className="ldh-adapted ldh-adapted--generic"><i /> Questions variées pour découvrir la méthode</span>}
                </div>
                <div className="ldh-session-demo" data-ldh-reveal="right" style={{ '--ldh-reveal-delay': '120ms' }}>
                    <MiniSession packKey={packKey} onComplete={setAuditSummary} onReset={() => setAuditSummary(null)} />
                </div>
            </section>

            <section className="ldh-memory-story">
                <div className="ldh-memory-copy" data-ldh-reveal="up">
                    <span className="ldc-section-no">03 — La mémoire en une image</span>
                    <h2>Sans rappel, la trace s’efface.<br /><em>Avec le bon rythme, elle tient.</em></h2>
                    <p>Chaque récupération réussie renforce la trace et permet d’espacer progressivement le prochain passage.</p>
                </div>
                <div className="ldh-curve-panel" data-ldh-reveal="panel" style={{ '--ldh-reveal-delay': '110ms' }}><span>La courbe de l’oubli — et comment la déjouer</span><ForgettingCurve /></div>
            </section>

            <section className="ldc-direction ldh-direction">
                <div className="ldc-direction-copy" data-ldh-reveal="left">
                    <span className="ldc-section-no">04 — Une direction claire</span>
                    <h2>{directionTitle}</h2>
                    <p>{directionText}</p>
                    <Link to="/inscription">Créer mon compte gratuit <ArrowRight size={16} /></Link>
                </div>
                <div className="ldh-direction-visual" data-ldh-reveal="right" style={{ '--ldh-reveal-delay': '110ms' }}>
                    <MasteryCard pack={pack} auditSummary={auditSummary} />
                </div>
            </section>

            <section className="ldc-access ldh-access">
                <div className="ldh-access-orbits" aria-hidden="true"><MemoryVisual /></div>
                <div className="ldh-access-head" data-ldh-reveal="up">
                    <span className="ldc-section-no">Une offre sans zone grise</span>
                    <h2>Commence gratuitement.<br /><em>Débloque seulement si la méthode te convient.</em></h2>
                    <p>L’essai permet de découvrir concrètement MonQuiz. L’abonnement ouvre ensuite tous les contenus et les outils de progression de la thématique choisie.</p>
                </div>
                <div className="ldh-access-grid">
                    <article className="ldh-plan ldh-plan--free" data-ldh-reveal="left" style={{ '--ldh-reveal-delay': '80ms' }}>
                        <div className="ldh-plan-top">
                            <span>Pour essayer</span>
                            <strong className="ldh-plan-price">0 €<small>sans carte bancaire</small></strong>
                        </div>
                        <h3>Découvrir la méthode</h3>
                        <ul>
                            <li><Check size={16} /> Guides et conseils en libre accès</li>
                            <li><Check size={16} /> Micro-séance immédiate, sans compte</li>
                            <li><Check size={16} /> Sujets d’essai après inscription gratuite</li>
                            <li><Check size={16} /> Corrections expliquées</li>
                            <li><Check size={16} /> Exemples de réponses rédigées corrigées, à lire sans compte</li>
                        </ul>
                        <Link to={user ? '/compte' : '/inscription'} className="ldh-plan-cta">
                            {user ? 'Accéder à mes quiz' : 'Créer mon compte gratuit'} <ArrowRight size={15} />
                        </Link>
                        <div className="ldh-plan-foot">Accès immédiat à la démonstration</div>
                    </article>
                    <article className="ldh-plan ldh-plan--full" data-ldh-reveal="right" style={{ '--ldh-reveal-delay': '160ms' }}>
                        <div className="ldh-plan-top">
                            <span>Pour préparer le concours</span>
                            <strong className="ldh-plan-price">dès {stats.prixMin} €<small>/ mois · paiement unique</small></strong>
                        </div>
                        <h3>Accéder à toute la thématique</h3>
                        <ul>
                            <li><Check size={16} /> Tous les sujets et toutes les questions</li>
                            <li><Check size={16} /> Révisions espacées et quiz d’erreurs</li>
                            <li><Check size={16} /> Mélanges de sujets et suivi par sujet</li>
                            <li><Check size={16} /> Questions à réponse rédigée, corrigées critère par critère, là où la thématique en propose</li>
                        </ul>
                        <a href="#thematiques" className="ldh-plan-cta ldh-plan-cta--soft">
                            Choisir ma thématique <ArrowRight size={15} />
                        </a>
                        <div className="ldh-plan-foot">Aucune reconduction automatique</div>
                    </article>
                </div>
            </section>

            {/* ===== Catalogue des thématiques (ancre du menu « Thématiques ») ===== */}
            {scopes.length > 0 && (() => {
                // Concours vs entraînement transversal : la catégorie du scope fait foi
                // quand elle est renseignée (admin), sinon repli par slug pour les
                // généralistes connus — « Retrouve ses sujets de QCM » ne doit
                // s'appliquer qu'aux vrais concours.
                const TRANSVERSAL_SLUGS = ['entrainer-ma-memoire', 'tests-psychotechniques'];
                const isTransversal = (s) => (s.category?.slug
                    ? ['entrainement', 'transversal', 'generaliste'].some(k => s.category.slug.includes(k))
                    : TRANSVERSAL_SLUGS.includes(s.slug));
                const concours = scopes.filter(s => !isTransversal(s));
                const transversaux = scopes.filter(isTransversal);
                const card = (scope) => (
                    <Link
                        key={scope.id}
                        to={user ? `/compte/quiz/${scope.slug}` : `/${scope.slug}`}
                        className="ldh-scope-card"
                        style={scope.image ? { backgroundImage: `linear-gradient(to bottom, rgba(10,12,24,.32) 0%, rgba(10,12,24,.78) 100%), url(${scope.image})` } : undefined}
                    >
                        {scope.free_until && (
                            <span className="ldh-scope-free">
                                🎁 Gratuit jusqu'au {new Date(scope.free_until + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                            </span>
                        )}
                        <span className="ldh-scope-icon ldh-scope-icon--illu">
                            <ScopeIllustration icon={scope.icon} size={20} />
                        </span>
                        <strong>{scope.label}</strong>
                        <span className="ldh-scope-meta">
                            {scope.nb_questions ? `${Number(scope.nb_questions).toLocaleString('fr-FR')} questions` : 'Sujets d\'essai gratuits'}
                        </span>
                        <span className="ldh-scope-cta">Découvrir <ArrowRight size={13} /></span>
                    </Link>
                );
                return (
                    <section className="ldh-scopes" id="thematiques">
                        <div className="ldh-scopes-head">
                            <span className="ldc-section-no">Choisis ta thématique</span>
                            <h2>Un concours en tête ?<br /><em>Retrouve ses sujets de QCM.</em></h2>
                        </div>
                        <div className="ldh-scopes-grid">{concours.map(card)}</div>
                        {transversaux.length > 0 && (
                            <>
                                <div className="ldh-scopes-sub">
                                    <strong>Et pour muscler le socle</strong>
                                    <span>Logique, mémoire, rapidité — des entraînements de fond, en complément d'une thématique de concours.</span>
                                </div>
                                <div className="ldh-scopes-grid ldh-scopes-grid--sub">{transversaux.map(card)}</div>
                            </>
                        )}
                        {/* Publications partenaires : catalogue signé, distinct des thématiques
                            éditoriales. La signature de l'auteur EST le message — ces contenus
                            sont des supports d'entraînement produits par un tiers identifié. */}
                        {partnerScopes.length > 0 && (
                            <>
                                <div className="ldh-scopes-sub">
                                    <strong>Thématiques d'experts</strong>
                                    <span>Conçues et signées par leurs auteurs, spécialistes de leur domaine.</span>
                                </div>
                                <div className="ldh-scopes-grid ldh-scopes-grid--sub">
                                    {partnerScopes.map(scope => (
                                        <Link
                                            key={scope.id}
                                            to={user ? `/compte/quiz/${scope.slug}` : `/${scope.slug}`}
                                            className="ldh-scope-card"
                                            style={scope.image ? { backgroundImage: `linear-gradient(to bottom, rgba(10,12,24,.32) 0%, rgba(10,12,24,.78) 100%), url(${scope.image})` } : undefined}
                                        >
                                            <span className="ldh-scope-icon ldh-scope-icon--illu">
                                                <ScopeIllustration icon={scope.icon} size={20} />
                                            </span>
                                            <strong>{scope.label}</strong>
                                            {scope.partner_name && <span className="ldh-scope-author">Par {scope.partner_name}</span>}
                                            {/* Fil d'Ariane de catégorie : rend la taxonomie visible dès la
                                                première publication. Quand il y en aura plusieurs, ces mêmes
                                                catégories deviendront les sous-titres de regroupement. */}
                                            {scope.category && (
                                                <span className="ldh-scope-cat">
                                                    {scope.category.parent ? `${scope.category.parent.name} · ` : ''}{scope.category.name}
                                                </span>
                                            )}
                                            <span className="ldh-scope-meta">
                                                {scope.nb_questions ? `${Number(scope.nb_questions).toLocaleString('fr-FR')} questions` : 'Sujets d\'essai gratuits'}
                                            </span>
                                            <span className="ldh-scope-cta">Découvrir <ArrowRight size={13} /></span>
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </section>
                );
            })()}

            <section className="ldc-final" data-ldh-reveal="panel">
                <span>La meilleure façon de comprendre MonQuiz reste de l’essayer.</span>
                <h2>Commence par un sujet d'essai.<br /><em>Vois ce que tu sais vraiment.</em></h2>
                <div className="ldc-actions ldc-actions--center">
                    {user ? (
                        <Link to="/compte" className="ldc-btn ldc-btn--dark">Accéder à mes quiz <ArrowRight size={17} /></Link>
                    ) : (
                        <Link to="/inscription" className="ldc-btn ldc-btn--dark">Créer mon compte gratuit <ArrowRight size={17} /></Link>
                    )}
                    <a href="#essai" className="ldc-text-link">Refaire la micro-séance</a>
                </div>
            </section>

            <SiteFooter />
        </div>
    );
}
